import React, { useRef } from 'react';
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { ROOMS, TOWN_LOCATIONS } from '../rooms';
import { useVenueAnchors } from '../hooks/useVenueAnchors';

// VenueFootprints — the plan view's measuring tape.
//
// Siting a venue puts an interior model at a building's coordinates, and until
// you can see both outlines on the same map there is no way to answer the only
// three questions that matter: how big is the shop, how big is the building it
// was put in, and is one of them wrong.
//
// So this draws BOTH, in plan, to the same scale:
//
//   dashed ring   the anchor BUILDING's footprint, measured off the city model
//   solid ring    the venue MODEL's own footprint, measured by MeshInterior
//
// When the solid ring is larger than the dashed one, the shop is bigger than the
// building it is standing in — which is not automatically wrong (an interior may
// legitimately exceed its shell; see the record store), but it IS why a sited
// room pokes through walls, and it should be a decision rather than a surprise.
//
// The interior outline is only known once someone has walked in — MeshInterior
// is the only thing that measures it, and it only exists while the room is
// mounted. The store caches it, so the first visit teaches the map forever.

// Ground clearance. High enough to clear kerbs and forecourts, which stand well
// above y=0 and would otherwise swallow the outline; depth test is off anyway,
// so this is really just keeping the two rings from z-fighting each other.
const Y_BUILDING = 0.5;
const Y_VENUE = 0.62;
const LABEL_Y = 0.8;

// ── The label has to be SCREEN-sized, not world-sized ───────────────────────
//
// drei's <Text fontSize> is in world units, and under an orthographic camera
// on-screen pixels = worldSize · camera.zoom. A size chosen at the whole-city
// fit is therefore multiplied by every step you zoom in: readable across the
// map, then a headline covering three blocks the moment you go to look at one.
// Which is precisely backwards, because zooming in is when you want to READ it.
//
// So the text is authored at fontSize 1 and the group it sits in is scaled by
// px / zoom every frame. On-screen height = 1 · (px/zoom) · zoom = px, constant
// at any zoom, which is what a map annotation should be. The OUTLINES stay in
// world units — those are the measurement, and a measurement that resized
// itself would be worthless.
const LABEL_PX = 13;
const LABEL_LIFT = 1.6;   // in label-space, so the gap is constant on screen too

// Exported for the Sites tool's ghosts (SiteSurvey), which annotate the same map.
export function PlanLabel({ pos, color, children }) {
  const ref = useRef();
  useFrame(({ camera }) => {
    if (!ref.current) return;
    const z = camera.isOrthographicCamera ? camera.zoom : 1;
    ref.current.scale.setScalar(LABEL_PX / Math.max(1e-4, z));
  });
  return (
    // Laid flat, and rotated so its own up-axis points to −z — which is what the
    // plan camera treats as screen-up, so the text reads the right way round.
    <group ref={ref} position={pos} rotation={[-Math.PI / 2, 0, 0]}>
      <Text
        position={[0, LABEL_LIFT, 0]}
        fontSize={1}
        color={color}
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.08}
        outlineColor="#000000"
        toneMapped={false}
      >
        {children}
      </Text>
    </group>
  );
}

// A rectangle as a line loop on the ground plane. Cheaper and crisper than a
// plane with a hole, and it never hides what it is measuring.
export function Outline({ cx, cz, w, d, color, y, dashed = false, opacity = 1 }) {
  const hw = Math.max(0.01, w / 2);
  const hd = Math.max(0.01, d / 2);
  const pts = [
    [cx - hw, y, cz - hd], [cx + hw, y, cz - hd],
    [cx + hw, y, cz + hd], [cx - hw, y, cz + hd],
    [cx - hw, y, cz - hd],
  ];
  if (!dashed) {
    return (
      <line>
        <bufferGeometry>
          <bufferAttribute attach="attributes-position" args={[new Float32Array(pts.flat()), 3]} />
        </bufferGeometry>
        <lineBasicMaterial color={color} transparent opacity={opacity} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
      </line>
    );
  }
  // Dashes built by hand rather than with LineDashedMaterial: that needs
  // computeLineDistances() on the geometry and a renderer-side dash scale, both
  // of which are one more thing to get wrong for a rectangle. Four sides chopped
  // into segments is the same picture with none of the setup.
  const segs = [];
  for (let i = 0; i < 4; i++) {
    const [ax, , az] = pts[i];
    const [bx, , bz] = pts[i + 1];
    const len = Math.hypot(bx - ax, bz - az);
    const n = Math.max(2, Math.round(len / 1.6));
    for (let j = 0; j < n; j += 2) {
      const t0 = j / n; const t1 = Math.min(1, (j + 1) / n);
      segs.push(
        ax + (bx - ax) * t0, y, az + (bz - az) * t0,
        ax + (bx - ax) * t1, y, az + (bz - az) * t1,
      );
    }
  }
  return (
    <lineSegments>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[new Float32Array(segs), 3]} />
      </bufferGeometry>
      <lineBasicMaterial color={color} transparent opacity={opacity} depthTest={false} depthWrite={false} fog={false} toneMapped={false} />
    </lineSegments>
  );
}

export default function VenueFootprints() {
  const anchors = useVenueAnchors();
  const boxes = useWorldStore((s) => s.venueBoxes);
  const sizes = useWorldStore((s) => s.venueFootprints);
  if (!anchors) return null;

  return (
    <group renderOrder={997}>
      {TOWN_LOCATIONS.map((loc) => {
        const key = loc.interiorId;
        const a = anchors[key];
        if (!a) return null;
        const room = ROOMS[key] || {};
        const sited = room.placement === 'sited';
        const box = boxes?.[key];
        const size = sizes?.[key];

        // Label text is the whole point of the overlay, so it says everything
        // needed to make the call in one line: what it is, how big the building
        // is, how big the interior is, and whether one fits in the other.
        // ONE SHORT LINE. VenueMarkers already floats the venue's name over the
        // same spot, so repeating it here was half the width of the label saying
        // nothing new — and with seven venues on one map, width IS the clutter.
        // This carries only what the marker cannot: the two measurements and the
        // verdict.
        const b = box ? `${box.size[0].toFixed(0)}×${box.size[2].toFixed(0)}` : '—';
        const s = size ? `${size[0].toFixed(0)}×${size[2].toFixed(0)}` : 'walk in to measure';
        let verdict = '';
        if (box && size) {
          const over = Math.max(size[0] / box.size[0], size[2] / box.size[2]);
          verdict = over > 1.05 ? ` · ${over.toFixed(1)}× OVER` : ' · fits';
        }

        return (
          <group key={key}>
            {/* The building it is anchored to — always drawn, because it is the
                thing you are choosing between when you move a venue. */}
            {box && (
              <Outline
                cx={box.center[0]} cz={box.center[2]}
                w={box.size[0]} d={box.size[2]}
                color={loc.accent} y={Y_BUILDING} dashed opacity={0.85}
              />
            )}
            {/* The interior itself. Centred on the ANCHOR, not on the building's
                box centre, because that is where MeshInterior actually puts it:
                origin = [anchor.x, 0, anchor.z]. Drawing it on the box centre
                would show a room sitting somewhere it is not, which is worse
                than not drawing it. */}
            {sited && size && (
              <Outline
                cx={a[0]} cz={a[2]}
                w={size[0]} d={size[2]}
                color={loc.accent} y={Y_VENUE}
              />
            )}
            {/* A cross on the anchor point — the exact coordinate the room is
                built around, which neither outline shows on its own. */}
            <Outline cx={a[0]} cz={a[2]} w={1.6} d={1.6} color="#ffffff" y={Y_VENUE} opacity={0.7} />
            <PlanLabel pos={[a[0], LABEL_Y, a[2]]} color={loc.accent}>
              {`${sited ? 'SITED  ' : ''}bldg ${b} · shop ${s}${verdict}`}
            </PlanLabel>
          </group>
        );
      })}
    </group>
  );
}
